import type { GROQButton, GROQImage } from "@/sanity/lib/definitions";
import type { PageEventSchema } from "@/sanity/pages/PageEventTemplate";

const image = (src: string, width = 26, height = 26): GROQImage => ({ src, width, height, name: "", lqip: "" });
const fact = (key: string, icon: string, strong: string, rest: string) => ({
  _key: key,
  icon: image(`/event/${icon}.svg`),
  text: [{ _key: key, _type: "block" as const, style: "normal", markDefs: [], children: [
    { _key: `${key}-strong`, _type: "span" as const, text: strong, marks: ["strong"] },
    { _key: `${key}-rest`, _type: "span" as const, text: ` ${rest}`, marks: [] },
  ] }],
});

// The label remains visible until marketing selects the registration destination.
export const monthlyUpdateCta: GROQButton = {
  cta: { name: "Monthly Update registration", title: "Register for the next update", action: "link" },
};

export const monthlyUpdateCopy = {
  countdownLabel: "Thursday, Oct 15 at 8 PM CEST (2 PM EDT US)",
  countdownTarget: "2026-10-15T18:00:00.000Z",
  navCtaTextMobile: "Live Event",
  titleLine1: "The Monthly",
  titleLine2: "Prop Trading Update",
  descriptionStrong: "On Thursday, Oct 15 we go through everything that moved in prop trading this month. Market news, what’s new at IQ Capital and live-only offers.",
  descriptionMuted: "",
  s2BadgeText: "Event contents",
  s2TitleRest: "What exactly happens in a",
  s2TitleItalic: "Prop Trading Update?",
  s2DescriptionMuted: "We break down what moved in the prop market over the last 30 days and show you what’s new at IQ Capital. Then Christoph Radecker answers your questions live. And at the end, the best offers of the month.",
  s2Cards: [
    { _key: "recap", tabLabel: "Market recap", title: "What moved in prop trading.", description: "Rule changes at the big firms, new platforms, payout trends. We cut a month of noise down to what actually affects your trading." },
    { _key: "updates", tabLabel: "IQ Capital updates", title: "What’s new at IQ Capital.", description: "New products, platforms and features, plus the month in numbers: payouts, top traders and what’s coming next. Straight from the team, before anyone else hears it." },
    { _key: "live", tabLabel: "Live only", title: "Your questions. Live-only offers.", description: "Christoph answers your questions live, and every update closes with an offer that's only available during the stream. Not on the website. Not in the newsletter. Not afterwards." },
  ],
  s4BadgeText: "The host",
  s4TitleRegular: "IQ Capital Founder",
  s4TitleItalic: "Christoph Radecker",
  s5BadgeText: "FAQ",
  s5TitleRegular: "Frequently asked",
  s5TitleItalic: "Questions",
  s5Items: [
    { _key: "happens", question: "What exactly happens in a Prop Trading Update?", answer: "We talk about what moved in the prop market over the last 30 days. Every change at IQ Capital: rules, products, payouts. A live Q&A with Christoph Radecker. And at the end, the best offers of the month." },
    { _key: "free", question: "Is the Prop Trading Update really free?", answer: "Yes. Registration and every monthly update are free. You only pay if you decide to take one of the live offers." },
    { _key: "register", question: "Do I have to register every month?", answer: "No. Register once and you'll get a reminder with the link before every update. You can unsubscribe at any time." },
    { _key: "duration", question: "How long is each update?", answer: "About 60 to 90 minutes, including the live Q&A." },
  ],
  s6TitleRegular: "Don't miss what's next",
  s6TitleItalic: "in Prop Trading",
  s6Description: "On Thursday, Oct 15 we open the October update: what moved in prop trading, what’s new at IQ Capital and the best offers of the month. The offers open during the stream and close when it ends. Everyone else reads about it later.",
  s6Stats: [
    { _key: "traders", value: "20,000+", label: "Active traders" },
    { _key: "payouts", value: "$3.5M", label: "Payouts" },
    { _key: "funding", value: "Up to $2M", label: "Funding available" },
  ],
  s6FooterLinks: [{"_key": "flink-0", "label": "Imprint", "url": "/imprint"}, {"_key": "flink-1", "label": "Privacy", "url": "/privacy-policy"}, {"_key": "flink-2", "label": "Terms of Use", "url": "/terms-of-use"}, {"_key": "flink-3", "label": "Risk Disclosure", "url": "/risk-disclosure"}],
  s6FooterCopyright: "© IQ Capital - All rights reserved",
} satisfies Partial<PageEventSchema>;

export const monthlyUpdateDefaults = {
  ...monthlyUpdateCopy,
  cta: monthlyUpdateCta,
  navCta: { cta: { ...monthlyUpdateCta.cta!, title: "Live Event ° Oct 15, 8 PM CEST" } },
  features: [
    { _key: "free", icon: image("/monthly-update/money.svg", 24, 24), text: "Free every month" },
    { _key: "new", icon: image("/monthly-update/panel.svg", 24, 24), text: "First look at what’s new" },
    { _key: "offers", icon: image("/monthly-update/wifi.svg", 24, 24), text: "Exclusive live-only offers" },
    { _key: "qa", icon: image("/monthly-update/star.svg", 24, 24), text: "Live Q&A with the founder" },
  ],
  s4Image: image("/monthly-update/host-portrait.png", 1102, 1447),
  s4Items: [
    fact("experience", "icon-hourglass", "Over 20 years of trading experience.", "Trading profitably for two decades."),
    fact("championship", "icon-trophy", "World Trading Championship 2022 (Futures):", "2nd place, with +97.3% return in just 90 days."),
    fact("monthly", "icon-calendar", "Monthly five-figure payouts", "at Topstep, Apex, MFF and other major prop firms — proven consistency."),
    fact("total", "icon-certificate", "More than 250,000 US dollars", "in cumulative payouts from various prop firms, all verified."),
    fact("founder", "icon-partnership", "Co-founder of The Trading Pit", "and former advisor for platforms such as NinjaTrader."),
  ],
  scrollingWords: { words: ["$3.5M+ paid out", "4,8 ★ from real traders", "20,000+ funded traders", "Trusted in 52 countries"] },
} satisfies Partial<PageEventSchema>;
